"use client";

import type React from "react";
import { useState, useRef, useEffect, useCallback } from "react";
import { Input } from "./ui/input";
import { ChevronDown, ChevronUp, Search, Loader2 } from "lucide-react";
import { CustomerService } from "@/services/CustomerService";
import { TagsService } from "@/services/TagService";
import { ProductsService } from "@/services/ProductsService";
import { debounce } from "lodash";

interface ComboOption {
  label: string;
  value: string;
}

// Modificar a interface ComboSearchProps para incluir uma propriedade maxWidth
interface ComboSearchProps {
  id: string;
  context: "customer" | "tag" | "product" | "custom";
  value: string;
  onSelect: (value: string) => void;
  customSearchList?: ComboOption[];
  placeholder?: string;
  className?: string;
  maxWidth?: string; // Nova propriedade para controlar a largura máxima
}

// Atualizar a desestruturação de props para incluir maxWidth com valor padrão
export const ComboSearch: React.FC<ComboSearchProps> = ({
  id,
  context,
  value,
  onSelect,
  customSearchList = [],
  placeholder,
  className = "",
  maxWidth = "300px", // Valor padrão para maxWidth
}) => {
  // Estados principais
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [options, setOptions] = useState<ComboOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // Adicionar um cache para armazenar os itens já buscados
  const [itemCache, setItemCache] = useState<Record<string, string>>({});

  // Refs
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const isInitialLoadRef = useRef(true);
  const isLoadingMoreRef = useRef(false);
  const isSearchingRef = useRef(false);
  const previousValueRef = useRef<string>("");

  // Serviços

  // Função para buscar dados - simplificada e com controle de estado
  const fetchData = useCallback(
    async (query: string, page: number) => {
      // Evitar chamadas duplicadas
      if (
        context === "custom" ||
        isLoadingMoreRef.current ||
        isSearchingRef.current
      ) {
        return;
      }

      const customerService = new CustomerService();
      const tagsService = new TagsService();
      const productsService = new ProductsService();

      // Marcar como carregando
      if (page === 1) {
        isSearchingRef.current = true;
      } else {
        isLoadingMoreRef.current = true;
      }

      setIsLoading(true);

      try {
        const pageSize = 100;
        let newOptions: ComboOption[] = [];
        let totalPages = 0;

        switch (context) {
          case "customer": {
            const customers = await customerService.findAll({
              query,
              page,
              pageSize,
            });
            newOptions = customers.data.map((customer) => ({
              label: customer.name,
              value: customer.id,
            }));
            totalPages = customers.pagination.pages;
            break;
          }

          case "tag": {
            const tags = await tagsService.findAll({
              query,
              page,
              pageSize,
            });
            newOptions = tags.data.map((tag) => ({
              label: tag.name,
              value: tag.id,
            }));
            totalPages = tags.pagination.pages;
            break;
          }

          case "product": {
            const products = await productsService.findAll({
              query,
              page,
              pageSize,
            });
            newOptions = products.data.map((product) => ({
              label: product.name,
              value: product.id,
            }));
            totalPages = products.pagination.pages;
            break;
          }
        }

        // Atualizar opções
        if (page === 1) {
          setOptions(newOptions);
        } else {
          setOptions((prev) => [...prev, ...newOptions]);
        }

        // Atualizar se há mais páginas
        setHasMore(page < totalPages);
        setCurrentPage(page);
      } catch (error) {
        console.error(`Erro ao buscar ${context}:`, error);
        setHasMore(false);
      } finally {
        setIsLoading(false);
        if (page === 1) {
          isSearchingRef.current = false;
        } else {
          isLoadingMoreRef.current = false;
        }
      }
    },
    [context]
  );

  // Modificar a função fetchItemById para usar o cache e evitar chamadas repetidas
  const fetchItemById = useCallback(
    async (itemId: string) => {
      // Verificar se o item já está no cache
      if (itemCache[itemId]) {
        setSelectedLabel(itemCache[itemId]);
        return;
      }

      // Verificar se já estamos buscando ou se o ID é o mesmo da última busca
      if (
        !itemId ||
        isSearchingRef.current ||
        previousValueRef.current === itemId
      )
        return;

      // Atualizar a referência do valor anterior
      previousValueRef.current = itemId;
      isSearchingRef.current = true;
      setIsLoading(true);

      try {
        let label = "";

        const customerService = new CustomerService();
        const tagsService = new TagsService();
        const productsService = new ProductsService();

        switch (context) {
          case "customer": {
            const customer = await customerService.findById(itemId);
            label = customer.name;
            break;
          }
          case "tag": {
            const tag = await tagsService.findById(itemId);
            label = tag.name;
            break;
          }
          case "product": {
            const product = await productsService.findById(itemId);
            label = product.name;
            break;
          }
        }

        // Atualizar o cache e o label
        if (label) {
          setItemCache((prev) => ({ ...prev, [itemId]: label }));
          setSelectedLabel(label);
        }
      } catch (error) {
        console.error(`Erro ao buscar ${context} por ID:`, error);
      } finally {
        setIsLoading(false);
        isSearchingRef.current = false;
      }
    },
    [context, itemCache]
  );

  // Debounce para busca - com tempo maior
  const debouncedSearch = useCallback(
    debounce((query: string) => {
      fetchData(query, 1);
    }, 500),
    [fetchData]
  );

  // Modificar o useEffect para evitar chamadas desnecessárias
  useEffect(() => {
    // Limpar o label se não houver valor
    if (!value) {
      setSelectedLabel("");
      return;
    }

    // Verificar se o valor mudou realmente
    if (previousValueRef.current === value) {
      return;
    }

    // Verificar se já temos o item nos options
    const existingOption = options.find((opt) => opt.value === value);
    if (existingOption) {
      setSelectedLabel(existingOption.label);
      // Atualizar o cache
      setItemCache((prev) => ({ ...prev, [value]: existingOption.label }));
      return;
    }

    // Se for custom, procurar na lista fornecida
    if (context === "custom") {
      const customOption = customSearchList.find((opt) => opt.value === value);
      if (customOption) {
        setSelectedLabel(customOption.label);
        // Atualizar o cache
        setItemCache((prev) => ({ ...prev, [value]: customOption.label }));
      }
      return;
    }

    // Verificar se já temos no cache
    if (itemCache[value]) {
      setSelectedLabel(itemCache[value]);
      return;
    }

    // Buscar o item por ID apenas se necessário
    fetchItemById(value);
  }, [value, options, context, customSearchList, fetchItemById, itemCache]);

  // Configurar o observador de interseção para paginação infinita
  useEffect(() => {
    // Limpar observador anterior
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    // Só configurar se o dropdown estiver aberto e houver mais itens
    if (isDropdownOpen && hasMore && loadingRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (
            entries[0].isIntersecting &&
            !isLoadingMoreRef.current &&
            !isSearchingRef.current
          ) {
            fetchData(searchTerm, currentPage + 1);
          }
        },
        { threshold: 0.5 }
      );

      observerRef.current.observe(loadingRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [isDropdownOpen, hasMore, currentPage, fetchData, searchTerm]);

  // Carregar dados iniciais quando o dropdown é aberto pela primeira vez
  useEffect(() => {
    if (isDropdownOpen) {
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;

        if (context === "custom") {
          setOptions(customSearchList);
          setHasMore(false);
        } else {
          fetchData("", 1);
        }
      }
    } else {
      // Não resetar isInitialLoadRef quando fechamos o dropdown
      // para evitar recarregar quando abrir novamente
    }
  }, [isDropdownOpen, fetchData, context, customSearchList]);

  // Efeito para fechar o dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Manipuladores de eventos
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);

    if (!isDropdownOpen) {
      setIsDropdownOpen(true);
    }

    // Resetar para a primeira página e buscar com o novo termo
    setCurrentPage(1);
    debouncedSearch(newValue);
  };

  const handleSelect = (value: string, label: string) => {
    onSelect(value);
    setSelectedLabel(label);
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    const newState = !isDropdownOpen;
    setIsDropdownOpen(newState);

    if (newState) {
      setSearchTerm("");

      // Se for custom, carregar a lista fornecida
      if (context === "custom") {
        setOptions(customSearchList);
        setHasMore(false);
      }
      // Não fazer busca automática aqui - será feita pelo useEffect
    }
  };

  // Função para obter o placeholder
  const getPlaceholder = () => {
    if (placeholder) return placeholder;

    switch (context) {
      case "customer":
        return "Selecione um cliente...";
      case "tag":
        return "Selecione uma tag...";
      case "product":
        return "Selecione um produto...";
      default:
        return "Selecione...";
    }
  };

  // Renderizar opções para o contexto custom
  useEffect(() => {
    if (context === "custom") {
      setOptions(customSearchList);
    }
  }, [context, customSearchList]);

  // Atualizar o div container para aplicar o maxWidth
  return (
    <div className={`relative ${className}`} style={{ maxWidth }}>
      <div className="relative">
        <Input
          ref={inputRef}
          id={id}
          value={isDropdownOpen ? searchTerm : selectedLabel}
          placeholder={getPlaceholder()}
          onFocus={() => {
            if (!isDropdownOpen) {
              toggleDropdown();
            }
          }}
          onChange={handleInputChange}
          className="pl-10 pr-8 w-full"
        />

        {/* Ícone de pesquisa (esquerda) */}
        <span className="absolute inset-y-0 left-2 flex items-center pointer-events-none">
          {isLoading ? (
            <Loader2 className="h-4 w-4 text-gray-500 animate-spin" />
          ) : (
            <Search className="h-4 w-4 text-gray-500" />
          )}
        </span>

        {/* Ícone de dropdown (direita) */}
        <button
          type="button"
          onClick={toggleDropdown}
          className="absolute inset-y-0 right-2 flex items-center text-gray-500 hover:text-gray-700 focus:outline-none"
        >
          {isDropdownOpen ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>
      </div>

      {/* Dropdown de sugestões */}
      {isDropdownOpen && (
        <ul
          ref={dropdownRef}
          className="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-md shadow-md max-h-48 overflow-y-auto"
          tabIndex={-1}
        >
          {options.length > 0 ? (
            <>
              {options.map((item) => (
                <li
                  key={item.value}
                  className={`px-4 py-2 hover:bg-gray-100 cursor-pointer ${
                    value === item.value ? "bg-gray-100" : ""
                  }`}
                  onMouseDown={() => handleSelect(item.value, item.label)}
                >
                  {item.label}
                </li>
              ))}

              {hasMore && (
                <div
                  ref={loadingRef}
                  className="px-4 py-2 text-center text-gray-500"
                >
                  {isLoading && isLoadingMoreRef.current ? (
                    <div className="flex items-center justify-center">
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Carregando mais...
                    </div>
                  ) : (
                    <span>Role para carregar mais</span>
                  )}
                </div>
              )}
            </>
          ) : (
            <li className="px-4 py-2 text-gray-500">
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Carregando...
                </div>
              ) : (
                "Nenhum resultado encontrado"
              )}
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default ComboSearch;
